import { Component, ElementRef, OnDestroy, OnInit, ViewChild, AfterViewInit, ViewEncapsulation } from '@angular/core';
import { ShareddtaService } from '@app/services/shareddta.service';
import { Router } from '@angular/router';
import { AddressService } from '@app/services/address.service';
import { AuthServiceService } from '@app/services/auth-service.service';
import { DialogService } from '@app/services/dialog.service';
import { ToastService } from '@app/services/toast.service';
import { ToastrService } from 'ngx-toastr';
import { Subscription, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { BreadcrumbService } from '@app/services/breadcrumb.service';

interface Address {
  id: number;
  fullAddress: string;
  latitude: number;
  longitude: number;
  city: string;
}

interface FilterChip {
  label: string;
  count: number;
  value: string;
  active: boolean;
}

@Component({
  selector: 'app-address-listing',
  templateUrl: './address-listing.component.html',
  styleUrl: './address-listing.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class AddressListingComponent implements OnInit, OnDestroy, AfterViewInit {
  // Search
  searchQuery: string = '';
  private searchSubject = new Subject<string>();

  // Filter chips
  filterChips: FilterChip[] = [
    { label: 'All', count: 0, value: 'all', active: true }
  ];

  // Filtered addresses (what's displayed)
  filteredAddresses: Address[] = [];

  // Pagination
  currentPage: number = 1;
  totalPages: number = 1;
  itemsPerPage: number = 20;
  selectedLocation: string = 'all';
  private addressListSub?: Subscription;
  isLoading: boolean = true;

  constructor(
    private router: Router,
    private addressService: AddressService,
    public authService: AuthServiceService,
    public toastr: ToastrService,
    private dialogService: DialogService,
    private toast: ToastService,
    private shareddta: ShareddtaService,
    private breadcrumbService: BreadcrumbService
  ) { }

  ngAfterViewInit(): void {
    this.shareddta.signalPageReady();
  }

  ngOnInit(): void {
    this.breadcrumbService.set([
      { label: 'Dashboard', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6', action: () => this.router.navigate(['/admin/dashboard']) },
      { label: 'Addresses' }
    ]);
    this.addressList(this.currentPage);

    // Setup search debounce
    this.searchSubject.pipe(
      debounceTime(500),
      distinctUntilChanged()
    ).subscribe(query => {
      const trimmedQuery = query.trim();
      if (trimmedQuery.length >= 2 || trimmedQuery.length === 0) {
        this.currentPage = 1;
        this.addressList(this.currentPage, trimmedQuery);
      }
    });
  }

  ngOnDestroy(): void {
    this.addressListSub?.unsubscribe();
    this.searchSubject.complete();
  }

  // Search addresses
  onSearch(): void {
    this.cross = this.searchQuery.trim().length > 0;
    this.searchSubject.next(this.searchQuery);
  }

  // Filter by chip
  onFilterClick(clickedChip: FilterChip): void {
    // Update active state
    this.filterChips.forEach(chip => {
      chip.active = chip === clickedChip;
    });

    this.selectedLocation = clickedChip.value;
    this.currentPage = 1;
    this.addressList(this.currentPage, this.searchQuery.trim());
  }

  // Add address
  addAddress(): void {
    this.router.navigate(['/admin/address/add']);
  }

  // View on map
  viewOnMap(address: Address): void {
    const mapsUrl = `https://maps.google.com/?q=${address.latitude},${address.longitude}`;
    window.open(mapsUrl, '_blank');
  }

  // Edit address
  @ViewChild('addresstableContainer', { static: false }) addresstableContainer!: ElementRef;
  editAddress(address: Address): void {
    console.log('Editing address:', address);
    const scrollPos = this.addresstableContainer.nativeElement.scrollTop;
    localStorage.setItem('addressListScroll', scrollPos.toString());
    this.router.navigate(['admin/address/edit'], { state: { address } });
  }

  // Get pagination info
  getPaginationInfo() {
    const total = this.addressDataCount;

    if (!total || total === 0) {
      return 'No addresses found';
    }

    this.totalPages = Math.ceil(total / this.itemsPerPage);
    const start = (this.currentPage - 1) * this.itemsPerPage + 1;
    const end = Math.min(this.currentPage * this.itemsPerPage, total);
    return `Showing ${start} - ${end} of ${total} Addresses`;
  }

  // Navigation
  goBack(): void {
    this.router.navigate(['/dashboard']);
  }

  cross: boolean = false;
  clearText() {
    this.searchQuery = '';
    this.cross = false;
    this.currentPage = 1;
    this.searchSubject.next(''); // Trigger list refresh via debounce logic
  }

  addressData: any[] = [];
  addressDataCount: number = 0;

  addressList(page?: number, search_text?: string) {
    this.addressListSub?.unsubscribe();
    this.isLoading = true;
    this.addressListSub = this.addressService
      .getAllAddressListing(page, this.selectedLocation, search_text)
      .subscribe(
        (data: any) => {
          this.isLoading = false;
          const sections = data?.sections || {};
          const selectedSection = this.extractSelectedSection(data?.selected_section);
          this.selectedLocation = selectedSection;
          this.filterChips = this.buildFilterChips(sections, selectedSection);

          const addresses = data?.addresses || {};
          this.addressData = addresses?.results || [];
          this.addressDataCount = addresses?.count || 0;
          this.totalPages = Math.max(Math.ceil(this.addressDataCount / this.itemsPerPage), 1);

          this.addressData.forEach((element: any, index: number) => {
            element.sNo = (index + 1) + (this.itemsPerPage * (this.currentPage - 1));
          });

          this.restoreScrollPosition();
        },
        (error: any) => {
          this.isLoading = false;
          this.authService.dataError(error);
        }
      );
  }

  goToPreviousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.addressList(this.currentPage, this.searchQuery.trim());
    }
  }

  goToNextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.addressList(this.currentPage, this.searchQuery.trim());
    }
  }

  async deleteOrder(addressId: any): Promise<void> {
    const confirmed = await this.dialogService.confirm(
      'Delete Address',
      'Are you sure you want to delete this address? This action cannot be undone.',
      'Delete',
      'Cancel'
    );

    if (confirmed?.confirmed) {
      this.addressService.addressDelete(addressId).subscribe(
        (data: any) => {
          if (this.authService.resultCodeError(data)) {
            return;
          }
          if (data.resultDescription) {
            this.toast.show('success', data.resultDescription);
            this.addressList(this.currentPage, this.searchQuery.trim());
          }
        },
        (error: any) => {
          this.authService.dataError(error);
        }
      );
    }
  }

  restoreScrollPosition(): boolean {
    const cachedScroll = localStorage.getItem('addressListScroll');
    if (cachedScroll) {
      setTimeout(() => {
        if (this.addresstableContainer) {
          this.addresstableContainer.nativeElement.scrollTop = +cachedScroll;
        }
      }, 50);
    }
    return true;
  }

  private buildFilterChips(sections: any, selectedSection: string): FilterChip[] {
    const chips: FilterChip[] = [];
    const normalizedSelected = this.normalizeSectionValue(selectedSection || 'all');

    chips.push({
      label: this.formatSectionLabel(sections?.all?.label || 'all'),
      count: sections?.all?.count || 0,
      value: 'all',
      active: normalizedSelected === 'all'
    });

    (sections?.top_locations || []).forEach((location: any) => {
      const value = location?.label || '';
      chips.push({
        label: this.formatSectionLabel(location?.label || ''),
        count: location?.count || 0,
        value,
        active: normalizedSelected === this.normalizeSectionValue(value)
      });
    });

    if (sections?.others) {
      chips.push({
        label: this.formatSectionLabel(sections.others.label || 'others'),
        count: sections.others.count || 0,
        value: 'others',
        active: normalizedSelected === 'others'
      });
    }

    return chips;
  }

  private formatSectionLabel(label: string): string {
    if (!label) return '';
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  private normalizeSectionValue(value: string): string {
    return (value || '').trim().toLowerCase();
  }

  private extractSelectedSection(selectedSection: any): string {
    if (typeof selectedSection === 'string') {
      return selectedSection;
    }
    if (selectedSection && typeof selectedSection === 'object') {
      return selectedSection.label || this.selectedLocation || 'all';
    }
    return this.selectedLocation || 'all';
  }
}